import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Printer, Send, FileText } from "lucide-react";
import { Account } from "@features/cx/outstanding/types";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useSendReminder } from "@features/cx/outstanding/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";

interface DisconnectionNoticeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  account: Account;
  outstandingAmount: number;
}

export function DisconnectionNoticeDialog({
  isOpen,
  onClose,
  account,
  outstandingAmount,
}: DisconnectionNoticeDialogProps) {
  const remoteUtilityId = useRemoteUtilityId();
  const sendReminder = useSendReminder();

  const customerEmail = `${account?.email
  }`;

  const formatCurrency = (amount: any) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const disconnectionDate = new Date();
  disconnectionDate.setDate(disconnectionDate.getDate() + 10); // 10 days from now

  const formFields: FormField[] = [
    {
      name: "additionalNotes",
      type: "textarea",
      label: "Additional Notes (Optional)",
      placeholder: "Enter any additional information for the customer...",
      rows: 3,
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      additionalNotes: "",
    },
  });

  const handleSendNotice = () => {
    const formValues = form.getValues();
    const reminderDetails = {
      remote_utility_id: remoteUtilityId,
      channel_type: "EMAIL",
      to: [account.email],
      body: formValues.additionalNotes,
      context: {
        entity_id: account.id,
        action_type: "disconnection_notice",
        reminder_type: "Notice",
      },
    };

    sendReminder.mutateAsync(reminderDetails, {
      onSuccess: () => {
        logEvent('Notice Generated', {
          channelType: "EMAIL",
        })
      }
    });
    form.reset();
    onClose();
  };

  const handlePrintNotice = () => {
    const formValues = form.getValues();
    const additionalNotes = formValues.additionalNotes;

    // Safe property access with fallbacks
    const accountNumber =
      account?.accountNumber || account?.consumerNo || "N/A";
    const customerName =
      account?.customerName ||
      `${account?.firstName || ""} ${account?.lastName || ""}`.trim() ||
      "N/A";
    const serviceAddress = account?.serviceAddress || "N/A";
    const customerType = account?.customerType || "N/A";
    const daysOutstanding =
      account?.daysOutstanding || account?.billData?.daysSinceLastBill || 0;
    const lastPaymentDate =
      account?.lastPaymentDate ||
      account?.billData?.lastPaymentDate ||
      new Date().toISOString();
    const lastPaymentAmount = account?.lastPaymentAmount || 0;
    const finalOutstandingAmount =
      outstandingAmount || account?.billData?.outstandingBalance || 0;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Disconnection Notice - ${accountNumber}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 40px; 
                line-height: 1.6; 
                background-color: #f9f9f9;
              }
              .container {
                max-width: 800px;
                margin: 0 auto;
                background-color: white;
                padding: 40px;
                box-shadow: 0 0 10px rgba(0,0,0,0.1);
              }
              .header { 
                text-align: center; 
                border-bottom: 2px solid #dc2626; 
                padding-bottom: 20px; 
                margin-bottom: 30px; 
              }
              .notice-box { 
                border: 3px solid #dc2626; 
                padding: 20px; 
                margin: 20px 0; 
                background-color: #fef2f2; 
                border-radius: 8px;
              }
              .warning { 
                color: #dc2626; 
                font-weight: bold; 
              }
              .section { 
                margin-bottom: 25px; 
              }
              .table { 
                width: 100%; 
                border-collapse: collapse; 
                margin: 15px 0; 
              }
              .table th, .table td { 
                border: 1px solid #ddd; 
                padding: 12px 8px; 
                text-align: left; 
              }
              .table th { 
                background-color: #f5f5f5; 
                font-weight: bold;
              }
              .footer { 
                margin-top: 40px; 
                font-size: 14px; 
                color: #666; 
                border-top: 1px solid #eee;
                padding-top: 20px;
              }
              .print-btn {
                background-color: #dc2626;
                color: white;
                padding: 12px 24px;
                border: none;
                border-radius: 6px;
                font-size: 16px;
                cursor: pointer;
                margin: 20px 0;
                display: block;
                margin-left: auto;
                margin-right: auto;
              }
              .print-btn:hover {
                background-color: #b91c1c;
              }
              h1, h2, h3 { 
                margin-top: 0; 
              }
              ul { 
                margin: 10px 0; 
                padding-left: 20px; 
              }
              li { 
                margin-bottom: 5px; 
              }
              @media print { 
                body { 
                  margin: 0; 
                  background-color: white;
                }
                .container {
                  box-shadow: none;
                  margin: 0;
                  padding: 20px;
                }
                .print-btn {
                  display: none;
                }
                .notice-box {
                  border: 2px solid #dc2626 !important;
                  background-color: #fef2f2 !important;
                }
                .warning {
                  color: #dc2626 !important;
                }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <button class="print-btn" onclick="window.print()">🖨️ Print This Notice</button>
              
              <div class="header">
                <h1 class="warning">FINAL NOTICE OF DISCONNECTION</h1>
                   <p>
  ${account.consumerMappingData
    ?.map((item) => item.utilityService)
    .reduce((acc, curr, index, arr) => {
      if (index === 0) return curr;
      if (index === arr.length - 1) return `${acc} & ${curr}`;
      return `${acc}, ${curr}`;
    }, "")}
</p>
                <p>Notice Date: ${formatDate(new Date().toISOString())}</p>
              </div>
              
              <div class="notice-box">
                <h2 class="warning">IMPORTANT: YOUR SERVICE WILL BE DISCONNECTED</h2>
                <p><strong>Disconnection Date:</strong> ${formatDate(
                  disconnectionDate.toISOString()
                )}</p>
              </div>

              <div class="section">
                <h2>Account Information</h2>
                <p><strong>Account Number:</strong> ${accountNumber}</p>
                <p><strong>Customer Name:</strong> ${customerName}</p>
                <p><strong>Service Address:</strong> ${account.addressData?.area}, ${account.addressData?.subArea}, ${account.addressData?.premise}</p>
                <p><strong>Customer Category:</strong> ${account.categoryDisplay}</p>
              </div>

              <div class="section">
                <h2>Outstanding Balance</h2>
                <table class="table">
                  <tr>
                    <td><strong>Total Amount Due:</strong></td>
                    <td style="font-size: 18px; font-weight: bold; color: #dc2626;">${formatCurrency(
                      account?.billData?.outstandingBalance
                    )}</td>
                  </tr>
                  <tr>
                    <td>Days Outstanding:</td>
                    <td>${account?.billData?.daysSinceLastBill} days</td>
                  </tr>
                  <tr>
                    <td>Last Payment Date:</td>
                    <td>${formatDate(account?.billData?.lastPaymentDate)}</td>
                  </tr>
                  <tr>
                    <td>Last Payment Amount:</td>
                    <td>${formatCurrency(account?.billData?.lastPayAmount)}</td>
                  </tr>
                </table>
              </div>

              <div class="section">
                <h2 class="warning">NOTICE TO CUSTOMER</h2>
                <p>Your water and sewer service will be <strong>DISCONNECTED</strong> on <strong>${formatDate(
                  disconnectionDate.toISOString()
                )}</strong> unless payment in full is received or satisfactory payment arrangements are made before that date.</p>
                
                <h3>To Avoid Disconnection:</h3>
                <ul>
                  <li>Pay the full amount of ${formatCurrency(
                    account?.billData?.outstandingBalance
                  )} immediately</li>
                  <li>Contact our office to arrange a payment plan</li>
                  <li>Provide proof of inability to pay if you qualify for assistance programs</li>
                </ul>

                <h3>After Disconnection:</h3>
                <ul>
                  <li>A reconnection fee will be charged</li>
                  <li>Full payment of outstanding balance will be required</li>
                  <li>Service restoration may take 24-48 hours after payment</li>
                  <li>A deposit may be required for reconnection</li>
                </ul>
              </div>
              <div class="footer">
                <p><strong>IMPORTANT:</strong> This notice is served in accordance with local utility regulations. Failure to respond may result in service disconnection and additional fees.</p>
                <p>Notice generated on: ${formatDate(
                  new Date().toISOString()
                )}</p>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center text-red-600">
            <FileText className="mr-2 h-5 w-5" />
            Send Disconnection Notice
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <h3 className="font-semibold text-red-800 mb-2">
                DISCONNECTION NOTICE
              </h3>
              <p className="text-sm text-red-700 mb-2">
                This notice will inform the customer that their service will be
                disconnected on <strong>{formatDate(disconnectionDate)}</strong>{" "}
                unless payment is received.
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-red-600 font-medium">Email:</span>
                <span className="font-medium">{customerEmail}</span>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Account Details</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Account:</span>
                    <span>{account?.consumerNo || account?.accountNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Customer:</span>
                    <span>
                      {account?.customerName ||
                        `${account?.firstName || ""} ${
                          account?.lastName || ""
                        }`.trim()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Category:</span>
                    <span>{account?.categoryDisplay}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Outstanding Balance</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount Due:</span>
                    <span className="font-bold text-red-600">
                      {formatCurrency(account?.billData?.outstandingBalance)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Days Outstanding:
                    </span>
                    <span>{account?.billData?.daysSinceLastBill} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Last Payment Date:
                    </span>
                    <span className="font-medium">
                      {formatDate(account?.billData?.lastPaymentDate)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSendNotice}
              onReset={onClose}
              gridColumns={1}
              responsiveColumns={{
                sm: 1,
                md: 1,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                spacing: "comfortable",
                classes: {
                  form: "space-y-6",
                  button: "px-4 py-2 rounded-md font-medium transition-colors",
                  buttonContainer:
                    "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  container: "space-y-1",
                },
              }}
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSendNotice} className="flex-1">
              <Send className="mr-2 h-4 w-4" />
              Send Notice
            </Button>
            <Button variant="outline" onClick={handlePrintNotice}>
              <Printer className="mr-2 h-4 w-4" />
              Preview & Print
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
